import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Code2 } from "lucide-react";
import { LangToggle } from "../lib/publicLang";

export default function MethodologyEn({ lang, setLang }) {
  const [verifCount, setVerifCount] = useState(null);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/reporting/verification-count`)
      .then((r) => r.json()).then((d) => setVerifCount(d.count)).catch(() => {});
  }, []);
  const S = ({ n, title, children }) => (
    <section className="mt-12">
      <h2 className="font-heading text-2xl font-bold mb-4"><span className="text-accent-blue mr-2">{n}.</span>{title}</h2>
      <div className="text-white/70 leading-relaxed space-y-3 text-sm">{children}</div>
    </section>
  );
  return (
    <div className="min-h-screen bg-bg text-white" data-testid="methodology-page-en">
      <nav className="border-b border-white/10 sticky top-0 backdrop-blur-lg bg-bg/80 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon.svg" alt="ANATRADEX" className="w-9 h-9 rounded-lg" width="36" height="36" />
            <div className="font-heading font-bold text-lg">ANATRADEX <span className="text-white/40 text-xs font-normal">/ Methodology</span></div>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/" className="text-sm text-white/60 hover:text-white flex items-center gap-1.5"><ArrowLeft size={14} /> Back</Link>
            <LangToggle lang={lang} setLang={setLang} />
          </div>
        </div>
      </nav>
      <article className="max-w-4xl mx-auto px-6 py-16">
        <h1 className="font-heading font-bold text-4xl md:text-5xl tracking-tight">Calculation methodology — exact, public, reproducible</h1>
        <p className="mt-6 text-white/60 leading-relaxed">
          Every metric on this platform is computed with the formulas below — <b className="text-white">no adjustment
          coefficients, no artificial bounds, no pre-decided verdicts</b>. Demo datasets are explicitly labeled
          as demos and never presented as real performance. This page exists so a skeptical reader can
          re-derive every number independently.
        </p>

        <S n={1} title="Why most retail backtests are wrong">
          <p>Retail strategy vendors routinely publish Sharpe ratios above 3. In institutional quantitative
          finance, a net Sharpe {'>'} 2 is already an outlier — and Harvey (2017, Presidential Address, Journal
          of Finance) estimates that most published findings in financial economics are likely false positives
          due to uncorrected multiple testing. The gap is explained by overfitting, look-ahead bias and
          selection bias — the three failure modes our test suite is designed to detect.</p>
        </S>

        <S n={2} title="Sharpe ratio & annualization">
          <p className="font-mono text-xs bg-black/40 border border-white/10 rounded-lg p-4">Sharpe = mean(r) / std(r, ddof=1) × √250</p>
          <p>Computed on per-trade returns, annualized with √250. No smoothing, no winsorization.</p>
        </S>

        <S n={3} title="Sign-flip permutation test">
          <p>Under H₀ (no edge), the sign of each trade return is random. We flip each sign with probability
          0.5 and recompute the Sharpe — <b className="text-white">2,000 iterations, deterministic seed 42817</b>
          (identical for demo and real data). p = P(Sharpe_H₀ ≥ Sharpe_observed).</p>
          <p><b className="text-white">Why sign-flip and not shuffle?</b> Shuffling the order of returns preserves
          mean and standard deviation, so the permuted Sharpe equals the observed one and the test degenerates
          (p = 1 always). Sign-flipping destroys the edge under H₀ while preserving the magnitude distribution.</p>
        </S>

        <S n={4} title="PBO — Probability of Backtest Overfitting">
          <p>Combinatorially symmetric cross-validation (Bailey, Borwein, López de Prado & Zhu, 2014): the
          strategy set is split into in-sample/out-of-sample combinations; PBO is the probability that the
          best in-sample strategy underperforms the median out-of-sample.</p>
          <p><b>Interpretation zones</b> (indicative thresholds, never an automatic verdict): PBO {'<'} 15% →
          edge very likely · 15-30% → watch closely · ≥ 30% → probable overfitting.</p>
        </S>

        <S n={5} title="Deflated Sharpe Ratio">
          <p>Bailey & López de Prado (2014): corrects the observed Sharpe for the number of trials, sample
          length, skewness and kurtosis. Reported as the probability that the true Sharpe exceeds zero
          after accounting for multiple testing.</p>
        </S>

        <S n={6} title="Sample-size honesty">
          <p>Below <b className="text-white">100 trades</b>, the statistical power of permutation tests and DSR
          collapses. Reports flag any series under 100 observations: a non-significant p-value on a small
          sample does not prove absence of edge, and a significant one may be unstable.</p>
        </S>

        <S n={7} title="Data provenance hierarchy">
          <p>Every report explicitly labels each data series: <b className="text-white">DEMO</b> (seeded example
          set, illustrates methodology) {'<'} <b className="text-white">SELF-REPORTED</b> (user-imported
          MT4/MT5/cTrader/CSV — integrity sealed by SHA-256 hash-chain at import, origin not third-party
          verified) {'<'} <b className="text-white">BROKER-VERIFIED</b> (read-only broker sync — in development).</p>
        </S>

        <S n={8} title="What we do NOT claim to be">
          <p>Not a regulated investment firm (no AMF, SEC/FINRA or FCA registration). Not investment advice.
          Not sub-millisecond HFT (Python/FastAPI — p50/p95/p99 latency measured continuously and published
          via <code className="text-accent-blue">/api/reporting/live-stats</code>). Regulatory-style data
          exports are demonstrative only and not submissible to any regulator.</p>
        </S>

        <section className="rounded-2xl border border-accent-blue/30 bg-accent-blue/5 p-6 mt-12" data-testid="reproducibility-section-en">
          <h3 className="font-heading text-xl mb-3 flex items-center gap-2"><Code2 size={18} /> Reproducibility — verify our math yourself</h3>
          <p className="text-sm text-white/70">Every PDF report prints its <b className="text-white">deterministic seed (42817)</b> and
          iteration count. The raw data behind every figure is exportable:</p>
          <ul className="mt-3 space-y-1.5 text-sm font-mono">
            <li><span className="text-accent-blue">GET /api/reporting/dd-pack-data</span> — JSON: per-trade returns, full permutation null distribution, PBO/DSR inputs, seed</li>
            <li><span className="text-accent-blue">GET /api/reporting/dd-pack-data?format=csv</span> — CSV of per-trade returns</li>
          </ul>
          <p className="mt-4 text-sm text-white/70">Reproduce the permutation p-value in ~15 lines of Python:</p>
          <div className="rounded-lg border border-white/10 bg-black/40 p-4 my-3 font-mono text-xs overflow-x-auto">
            <pre className="text-white/80 overflow-x-auto"><code>{`import json, random, urllib.request
BASE = "${typeof window !== "undefined" ? window.location.origin : "https://anatradex.com"}"
data = json.load(urllib.request.urlopen(
    BASE + "/api/reporting/dd-pack-data?use_real_trades=false"))
sym = data["per_symbol"][0]
r, n = sym["returns"], len(sym["returns"])

def sharpe(x):
    m = sum(x) / len(x)
    sd = (sum((v - m) ** 2 for v in x) / (len(x) - 1)) ** 0.5
    return m / sd * 250 ** 0.5 if sd else 0

rng = random.Random(sym["perm_seed"])   # 42817
count = 0
for _ in range(sym["perm_n_iters"]):
    flipped = [v * (1 if rng.random() > 0.5 else -1) for v in r]
    if sharpe(flipped) >= sharpe(r):
        count += 1
p = count / sym["perm_n_iters"]
assert round(p, 4) == sym["permutation_p"]  # exact match`}</code></pre>
          </div>
          <p className="text-sm text-white/70">The <Link to="/audit" className="text-accent-blue underline">/audit page</Link> verifies the
          hash-chained event log (the last block hash is printed in every PDF). Same inputs → same outputs, always.</p>
          {verifCount > 0 && (
            <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-up/30 bg-up/5 px-3.5 py-1.5 text-xs text-up">
              {verifCount.toLocaleString("en-US")} raw-data export{verifCount > 1 ? "s" : ""} — each one is a possible independent verification
            </div>
          )}
        </section>

        <p className="mt-12 text-xs text-white/40">
          References: Bailey & López de Prado (2014), <i>The Deflated Sharpe Ratio</i> · Bailey, Borwein, López de
          Prado & Zhu (2014), <i>The Probability of Backtest Overfitting</i> · Harvey (2017), <i>Presidential
          Address: The Scientific Outlook in Financial Economics</i> · López de Prado (2018), <i>Advances in
          Financial Machine Learning</i>. Full French version: <Link to="/methodology" onClick={() => setLang("fr")} className="text-accent-blue underline">switch to FR</Link>.
        </p>

        <p className="mt-8 text-[11px] text-white/35 leading-relaxed" data-testid="methodology-risk-disclaimer-en">
          ⚠️ ANATRADEX is a statistical analysis tool, not investment advice. ANATRADEX is not a regulated investment
          firm and is not registered with any financial authority (including the AMF, SEC/FINRA or FCA). Trading
          carries a risk of loss of up to 100% of invested capital — 70-85% of retail traders lose money (ESMA).
          Past performance does not guarantee future results.
        </p>
      </article>
    </div>
  );
}
